import { Injectable } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { first, of, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';

import { filterBoolean } from '@recruitee/common';
import { createEffectFactory } from '@recruitee/ngrx';
import type { SupportedThemesT, ThemeName } from '@recruitee/theme';
import { RECRUITEE_THEME_LS_KEY } from '@recruitee/theme';

import { CurrentUserPreferencesHttpClient } from './current-user-preferences-http-client';
import {
  CurrentUserPreferencesActions,
  CurrentUserPreferencesSelectors,
} from './current-user-preferences.store';

@Injectable({ providedIn: 'root' })
export class CurrentUserPreferencesEffects {
  public effects = createEffectFactory(CurrentUserPreferencesActions, this.actions$);

  public fetchCurrentUserPreferences$ = this.effects(CurrentUserPreferencesActions.fetch, () =>
    this.currentUserPreferencesService
      .loadCurrentUserPreferences()
      .pipe(tap(data => this.cacheTheme(data.preferences.theme))),
  );

  public updatePreferredTheme$ = this.effects(
    CurrentUserPreferencesActions.updatePreferredTheme,
    ({ payload }) => {
      const themeName = <SupportedThemesT>payload;
      this.cacheTheme(themeName);

      return this.store.select(CurrentUserPreferencesSelectors.data).pipe(
        filterBoolean,
        first(),
        switchMap(data =>
          of(
            CurrentUserPreferencesActions.update({
              payload: {
                ...data,
                preferences: {
                  ...data.preferences,
                  theme: themeName,
                },
              },
            }),
          ),
        ),
      );
    },
  );

  public updatePipelinePromoteBannerDismissedOfferIds$ = this.effects(
    CurrentUserPreferencesActions.updatePipelinePromoteBannerDismissedOfferIds,
    ({ payload }) =>
      this.store.select(CurrentUserPreferencesSelectors.data).pipe(
        filterBoolean,
        first(),
        switchMap(data =>
          of(
            CurrentUserPreferencesActions.update({
              payload: {
                ...data,
                preferences: {
                  ...data.preferences,
                  pipelinePromoteBannerDismissedOfferIds: payload,
                },
              },
            }),
          ),
        ),
      ),
  );

  public update$ = this.effects(CurrentUserPreferencesActions.update, ({ payload }) =>
    this.currentUserPreferencesService.updateCurrentUserPreferences(payload),
  );

  constructor(
    private store: Store,
    private actions$: Actions,
    private currentUserPreferencesService: CurrentUserPreferencesHttpClient,
  ) {}

  private cacheTheme(theme: ThemeName): void {
    localStorage.setItem(RECRUITEE_THEME_LS_KEY, theme);
  }
}
